import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePlanningAssignDutyStore } from '@/rennovation/planning-view/store/usePlanningAssignDutyStore';
import { usePlanningStore } from '@/rennovation/planning-view/store/usePlanningStore';
import type { AssignDutyApiPayload } from '@/rennovation/planning-view/types/api/assign-duty-api-response';
import type { CreateDutyPayload, DutyDayType } from '@/rennovation/planning-view/types/payloads/duty-payloads';
import { useSetupBridgeStore } from '@/rennovation/planning-view/store/bridge/setupBridge';
import { useAlertStore } from '@/rennovation/planning-view/store/useAlertStore';
import { useDayStore } from '@/rennovation/planning-view/store/useDayStore';

export interface AssignDutyFormState {
	driverFixed: boolean;
	driverId: string | null;
	type: string;
	disposition: string | null;
}

const getDefaultForm = (): AssignDutyFormState => ({
	driverFixed: false,
	driverId: null,
	type: 'duty',
	disposition: null,
});

const NON_WORK_DUTY_NAMES = {
	reserve: 'SD',
	rest: 'R',
	compensation: 'A',
} as const;

const NON_WORK_DUTY_NAME_KEYS: Record<Exclude<DutyDayType, 'duty'>, CreateDutyPayload['nameKey']> = {
	reserve: 'duty_dayType_reserve_short',
	compensation: 'duty_dayType_compensation_short',
	rest: 'duty_dayType_rest_short',
};

export function useAssignDutyForm() {
	const { t } = useI18n();
	const assignDutyStore = usePlanningAssignDutyStore();
	const planningStore = usePlanningStore();
	const setupBridge = useSetupBridgeStore();
	const alertStore = useAlertStore();
	const dayStore = useDayStore();

	onMounted(async () => {
		await setupBridge.ensureLocale();
	});

	const form = ref<AssignDutyFormState>(getDefaultForm());
	const isSaving = ref(false);
	const savedDisposition = ref<string | null>(null);

	const typeOptions = computed(() => {
		const options = [
			{ label: t('duty_dayType_duty'), value: 'duty' },
			{ label: t('duty_dayType_reserve'), value: 'reserve' },
		];

		if (resolvedDriverId.value) {
			options.push({ label: t('duty_dayType_compensation'), value: 'compensation' }, { label: t('duty_dayType_rest'), value: 'rest' });
		}

		return options;
	});

	const hasDriverSelected = computed(() => !!resolvedDriverId.value);

	const driverOptions = computed(() =>
		planningStore.allUsers.map((user) => ({
			label: `${user.lastName}, ${user.firstName}`,
			value: user.id,
		}))
	);

	const isWorkDay = computed(() => form.value.type === 'duty');

	const resolvedDriverId = computed(() => (assignDutyStore.isDriverLocked ? assignDutyStore.preselectedDriverId : form.value.driverId));

	const isFormValid = computed(() => {
		const hasType = !!form.value.type;
		return isWorkDay.value ? hasType && !!form.value.disposition : hasType;
	});

	const assignDutyPropertyFixed = (day) => {
		return ['past', 'published'].includes(day?.status);
	};

	const isDefaultForFixed = computed(() => {
		const targetDate = assignDutyStore.targetDate;
		const day = dayStore.days[targetDate]?.data;
		return day && resolvedDriverId.value ? !assignDutyPropertyFixed(day) : false;
	});

	const formattedDate = computed(() => {
		const date = assignDutyStore.targetDate;
		if (!date) return '—';

		const d = new Date(date);
		if (isNaN(d.getTime())) return '—';

		return d.toLocaleDateString(setupBridge.activeLocale, {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
		});
	});

	watch(
		() => assignDutyStore.isModalOpen,
		(opened) => {
			if (opened) {
				form.value = {
					...getDefaultForm(),
					driverId: assignDutyStore.preselectedDriverId,
				};
				savedDisposition.value = null;
			}
		}
	);

	watch(
		() => form.value.type,
		(type) => {
			if (type !== 'duty') {
				if (form.value.disposition) {
					savedDisposition.value = form.value.disposition;
				}
				form.value.disposition = null;
			} else if (savedDisposition.value !== null) {
				form.value.disposition = savedDisposition.value;
			}
			if (isDefaultForFixed.value) {
				form.value.driverFixed = true;
			}
		}
	);

	watch(
		() => form.value.driverId,
		(driverId) => {
			if (isDefaultForFixed.value) {
				form.value.driverFixed = true;
			} else if (!driverId) {
				form.value.driverFixed = false;
			}
		}
	);

	watch(
		() => form.value.disposition,
		(disposition) => {
			if (isDefaultForFixed.value) {
				form.value.driverFixed = true;
			}
		}
	);

	const handleSave = async (): Promise<void> => {
		if (!isFormValid.value) return;

		isSaving.value = true;
		try {
			if (isWorkDay.value) {
				const dutyId = form.value.disposition!;
				const payload: AssignDutyApiPayload = {
					user: resolvedDriverId.value,
					fixed: resolvedDriverId.value ? form.value.driverFixed : false,
					fixedBy: null,
					day: assignDutyStore.currentDayId,
				};
				await assignDutyStore.assignDutyToDriver(dutyId, payload);
				const message = resolvedDriverId.value ? t('solution.assignDuty_success') : t('duty_unassign_success');
				alertStore.showAlert(message, 'success');
			} else {
				const currentType = form.value.type as Exclude<DutyDayType, 'duty'>;
				const payload: CreateDutyPayload = {
					user: resolvedDriverId.value,
					type: form.value.type as Exclude<DutyDayType, 'duty'>,
					fixed: resolvedDriverId.value ? form.value.driverFixed : false,
					fixedBy: null,
					day: assignDutyStore.currentDayId!,
					name: NON_WORK_DUTY_NAMES[currentType],
					nameKey: NON_WORK_DUTY_NAME_KEYS[currentType],
				};
				await assignDutyStore.createStatusDuty(payload);
				alertStore.showAlert(t('duty_create_success'), 'success');
			}

			assignDutyStore.closeModal();
		} catch (error) {
			console.error('[AssignDutyForm] Action pipeline execution error:', error);

			const fallbackMsg = t('duty_assignment_failed');
			const backendMsg = error?.response?.data?.message || error?.message;

			alertStore.showAlert(backendMsg || fallbackMsg, 'error');
		} finally {
			isSaving.value = false;
		}
	};

	return {
		form,
		isSaving,
		isWorkDay,
		isFormValid,
		typeOptions,
		driverOptions,
		formattedDate,
		assignDutyStore,
		hasDriverSelected,
		handleSave,
	};
}
